import {showNotification} from 'platform/components';

import {isNil} from 'ramda';

import i18n from '@dms/i18n';

import {downloadImageFile, Nullish} from 'shared/web';

import {
  PHOTO_DOWNLOAD_FLOOR_WIDTH,
  PHOTO_DOWNLOAD_SIZE_PRESETS,
  PhotoDownloadSizePreset,
} from '../constants/photoDownloadSizePresets';
import {getResizedStoragePhotoUrl} from './getResizedStoragePhotoUrl';

const BYTES_IN_MEGABYTE = 1024 * 1024;
// The storage resize endpoint always encodes JPEG.
const RESIZED_PHOTO_EXTENSION = 'jpg';
const FILE_EXTENSION_REGEX = /\.[^./]+$/;
const DEFAULT_PHOTO_FILE_NAME = 'photo';
const FILE_NAME_QUERY_PARAM = 'filename';

const fetchPhoto = async (url: string) => {
  // Storage redirects to the actual file.
  const response = await fetch(url, {redirect: 'follow'});
  if (!response.ok) {
    throw new Error(`Photo download failed with status ${response.status}`);
  }
  return response.blob();
};

const getResizedPhotoFileName = (url: string, fileName: string | Nullish) => {
  const photoUrl = new URL(url, window.location.href);
  // Same `filename` query param as the original download, then the storage file ID.
  const baseName =
    fileName ??
    photoUrl.searchParams.get(FILE_NAME_QUERY_PARAM) ??
    photoUrl.pathname.split('/').pop() ??
    '';
  const nameWithoutExtension = baseName.replace(FILE_EXTENSION_REGEX, '');

  return `${nameWithoutExtension || DEFAULT_PHOTO_FILE_NAME}.${RESIZED_PHOTO_EXTENSION}`;
};

const saveBlob = (blob: Blob, fileName: string) => {
  const blobUrl = URL.createObjectURL(blob);

  const temporaryLink = document.createElement('a');
  temporaryLink.href = blobUrl;
  temporaryLink.download = fileName;
  temporaryLink.click();

  URL.revokeObjectURL(blobUrl);
};

/**
 * Downloads one photo scaled down by the storage service to fit the preset's size limit. When the
 * preset width is still too large it retries at `PHOTO_DOWNLOAD_FLOOR_WIDTH`; when even that is
 * too large the photo is still downloaded and the user is warned. The original file is downloaded
 * for the `original` preset and for URLs the storage service cannot resize.
 */
export async function downloadCompressedPhoto(
  url: string,
  preset: PhotoDownloadSizePreset,
  fileName?: string | Nullish
) {
  const {maxBytes, width} = PHOTO_DOWNLOAD_SIZE_PRESETS[preset];
  const presetUrl = isNil(width) ? null : getResizedStoragePhotoUrl(url, width);
  const floorUrl = getResizedStoragePhotoUrl(url, PHOTO_DOWNLOAD_FLOOR_WIDTH);

  if (isNil(maxBytes) || isNil(width) || isNil(presetUrl) || isNil(floorUrl)) {
    return downloadImageFile(url);
  }

  const presetPhoto = await fetchPhoto(presetUrl);
  const photo =
    presetPhoto.size > maxBytes && width > PHOTO_DOWNLOAD_FLOOR_WIDTH
      ? await fetchPhoto(floorUrl)
      : presetPhoto;

  if (photo.size > maxBytes) {
    showNotification.warning(
      i18n.t('entity.photo.notifications.oversizedPhoto', {
        size: `${(photo.size / BYTES_IN_MEGABYTE).toFixed(1)} MB`,
      })
    );
  }

  saveBlob(photo, getResizedPhotoFileName(url, fileName));
}
